import { ViewSize } from '@storefront/hooks/useCheckViewSize';
import { isBoolean } from 'lodash';
import { env } from 'next-runtime-env';

import {
  BundleProductWithLowestPrice,
  BundleItemWithLowestPrice,
  BundleItemOptionWithLowestPrice,
} from '@storefront/app/v2/pdp-config/printer/product-configuration/product-configuration.utils';
import { getRegularPrice } from '@storefront/utils/bundlePriceCalculator';

export const getRequiredItems = (items: BundleItemWithLowestPrice[]) =>
  items?.filter((item) => item.required === true) || [];

export const getOptionalItems = (items: BundleItemWithLowestPrice[]) =>
  items?.filter((item) => item.title === 'Instant Paper') || [];

export const getOptionsForRequiredProducts = (
  requiredItems: BundleItemWithLowestPrice[]
) =>
  requiredItems.reduce((newOptions, item) => {
    if (item.options) {
      newOptions = newOptions.concat(item.options);
    }
    return newOptions;
  }, []);

export const requiredOptionsSelected = (
  selected_options: string[],
  requiredItems: BundleItemWithLowestPrice[]
) => {
  // handle cases when printer is not selected yet
  if (requiredItems.length === 0) {
    return false;
  }

  return requiredItems.every((requiredItem) =>
    selected_options.some((selectedOption) =>
      requiredItem.options?.some((option) => option?.uid === selectedOption)
    )
  );
};

export const optionalOptionsSelected = (
  optionalItems: BundleItemWithLowestPrice[],
  hasInstantPaper: boolean
) => {
  // handle cases when printer is not selected yet
  if (optionalItems.length === 0) {
    return true;
  }
  return optionalItems && hasInstantPaper;
};

export const getActiveItemInfo = (
  itemUid: any,
  optionUid: any,
  selectedProductItems: BundleItemWithLowestPrice[]
) =>
  selectedProductItems
    ?.find((item) => item.uid === itemUid)
    ?.options.find((option) => option.uid === optionUid);

export const hasUserSelectedPaper = (
  activeSKU: string,
  bundleProduct: BundleProductWithLowestPrice
) => {
  if (typeof sessionStorage === 'undefined') return false;
  const paperOptionUid = bundleProduct?.items?.find(
    (item) => item?.title === 'Instant Paper'
  )?.uid;
  const productConfiguration = sessionStorage.getItem('product-configuration');
  const parsedproductConfiguration = productConfiguration
    ? JSON.parse(productConfiguration)
    : null;
  const selectionsKeys = parsedproductConfiguration?.[activeSKU]
    ? Object.keys(parsedproductConfiguration?.[activeSKU])
    : [];

  if (selectionsKeys.includes(paperOptionUid)) {
    const bundleItem = bundleProduct?.items
      ?.find((item) => item?.uid === paperOptionUid)
      ?.options.find(
        (option) =>
          option.uid ===
          parsedproductConfiguration?.[activeSKU]?.[paperOptionUid]
      );

    return getRegularPrice(bundleItem) !== 0; // Check if paper option is not the zero-priced one
  }

  return false;
};

export const getBundleLevelImage = ({
  activeSKU,
  bundleProduct,
  viewSize,
  isSMB,
  paperSelected,
}: {
  activeSKU: string;
  bundleProduct: BundleProductWithLowestPrice;
  viewSize: ViewSize;
  isSMB?: boolean;
  paperSelected?: boolean;
}) => {
  const { isMobile, isTablet } = viewSize;
  const smbImageKey = isSMB ? '_SMB' : '';
  let noSelectionImage = env(
    `NEXT_PUBLIC_NO_SELECTION_REVIEW_NARITA_DESKTOP_URL${smbImageKey}`
  );

  if (isMobile) {
    noSelectionImage = env(
      `NEXT_PUBLIC_NO_SELECTION_REVIEW_NARITA_MOBILE_URL${smbImageKey}`
    );
  }
  if (isTablet) {
    noSelectionImage = env(
      `NEXT_PUBLIC_NO_SELECTION_REVIEW_NARITA_TABLET_URL${smbImageKey}`
    );
  }

  if (!activeSKU) return noSelectionImage || '';

  const userHasSelectedPaper = isBoolean(paperSelected)
    ? paperSelected
    : hasUserSelectedPaper(activeSKU, bundleProduct);

  if (isMobile || isTablet) {
    return userHasSelectedPaper
      ? bundleProduct?.small_accessories?.url
      : bundleProduct?.small_image?.url;
  }

  return userHasSelectedPaper
    ? bundleProduct?.base_accessories?.url
    : bundleProduct?.image?.url;
};

export const getImageClass = (
  activeSKU: string,
  className: string,
  loadingImage: boolean
) => {
  const suffix = loadingImage ? '-loading' : '';
  const noSelection = activeSKU ? '' : '-no-selection';
  return `${className}${noSelection}${suffix}`;
};

export const shouldShowDescriptions = (
  activeItem: BundleItemOptionWithLowestPrice,
  planType: Record<string, string>
) => {
  if (
    activeItem?.price === 0 &&
    activeItem.product.hp_product_type_label === 'instantPaper'
  )
    return;
  return planType?.[activeItem?.product.hp_product_type_label];
};
